import {action, computed, reaction, IReactionDisposer, toJS, runInAction, observe} from 'mobx';
import {IMAGE_MODES} from '../../../states/dashboard/main/main.settings';
import AnnotationBarController from '../ts-annotation-bar/ts-annotation-bar.controller';
import {ILocalizationService} from '../../services/LocalizationService';
import {mediaFrameUIParams} from '../../constants/media-frame-ui-params/media-frame-ui-params.constant';
import type {ImageEditorState} from './ts-image-editor.state';

const MAX_TAG_LENGTH = 140;

export interface ITsImageEditorDependencies {
    annotateService: any;
    tsScanArea: any;
    environmentDetect: any;
    tsInterfaceHelper: any;
    localizationService: ILocalizationService;
    window: Window;
    dashboardState: any; // Pass the whole state so we can observe currentResource
    videoModeState: any; // Video mode state for video control tracking
    displayTabletAsDesktop: boolean;
    mainController?: any; // Optional: for accessing main controller properties
    accountData?: any; // Optional: for accessing account settings
    dashboardController?: any; // Optional: for accessing dashboard controller properties
}

export interface ITsImageEditorController {
    readonly currentResource: any;
    readonly shouldShowImageEditor: boolean;
    readonly annotationBarController: any;
    readonly tagsOpen: boolean;
    readonly tags: string[];
    readonly maxTagLength: number;
    readonly isSelectionMode: boolean;
    readonly enableAnnotation: boolean;
    readonly labelMode: boolean;
    readonly isScanCaptureDesign: boolean;
    readonly isRoundedFloatingVideo: boolean;
    readonly isStretchedVideoMode: boolean;
    readonly enableTagging: boolean;
    readonly showAnnotationBar: boolean;
    readonly showSharingMode: boolean;
    readonly sharingModeActions: any[];
    readonly sharingModeData: any;
    readonly isShareResourceReady: boolean;
    readonly onShareClick: any;
    readonly onShareActionClick: any;
    readonly dashboardTags: any[];
    readonly isCanvasReadyWithImage: boolean;

    setMainController(mainController: any): void;
    setContainer(container: HTMLDivElement): void;
    setCanvas(canvas: HTMLCanvasElement): void;
    setImage(image: any): void;
    setLabelMode(value: boolean): void;
    closeTags(): void;
    openTagsPanel(e?: Event): void;
    toggleTags(): void;
    handleTagButtonClick(e: Event): void;
    updateTags(tags: string[]): void;
}

export class TsImageEditorController implements ITsImageEditorController {
    private readonly annotateService: any;
    private readonly tsScanArea: any;
    readonly environmentDetect: any;
    readonly displayTabletAsDesktop: boolean;
    readonly tsInterfaceHelper: any;
    readonly annotationBarController: AnnotationBarController;

    private readonly dashboardState: any;
    private readonly videoModeState: any;
    private mainController: any;
    private readonly accountData: any;
    private readonly dashboardController: any;
    private readonly window: Window;
    private container: HTMLDivElement | null = null;
    private isMobile: boolean;
    private currentResourceReactionDisposer?: IReactionDisposer;
    private imageModeReactionDisposer?: IReactionDisposer;
    private tagsChangedReactionDisposer?: IReactionDisposer;
    public canvas: HTMLCanvasElement | null = null;
    private currentLoadedImageUrl: string | null = null;

    constructor(dependencies: ITsImageEditorDependencies) {
        this.annotateService = dependencies.annotateService;
        this.tsScanArea = dependencies.tsScanArea;
        this.environmentDetect = dependencies.environmentDetect;
        this.displayTabletAsDesktop = dependencies.displayTabletAsDesktop;
        this.tsInterfaceHelper = dependencies.tsInterfaceHelper;
        this.dashboardState = dependencies.dashboardState;
        this.videoModeState = dependencies.videoModeState;
        this.mainController = dependencies.mainController;
        this.accountData = dependencies.accountData;
        this.dashboardController = dependencies.dashboardController;
        this.window = dependencies.window;

        this.annotationBarController = new AnnotationBarController(
            this.annotateService,
            this.dashboardState.imageEditorState,
            dependencies.localizationService.translate
        );
        this.isMobile = this.environmentDetect.isMobile(this.displayTabletAsDesktop);

        // Watch currentResource changes and call setImage
        this._initCurrentResourceWatcher();

        // Watch imageMode changes for scan mode
        this._initImageModeWatcher();

        this.annotateService.on('canvas:imageReady', this.onCanvasImageReady);
    }

    get imageEditorState(): ImageEditorState {
        return this.dashboardState.imageEditorState;
    }

    private onCanvasImageReady = () => {
        runInAction(() => {
            this.imageEditorState.isCanvasReadyWithImage = true;
        });
    };

    private _initCurrentResourceWatcher(): void {
        this.currentResourceReactionDisposer = reaction(
            () => ({
                resource: this.dashboardState?.currentResource,
                meetingMode: this.dashboardState?.meetingMode,
                videoControlActive: this.videoModeState?.isActive
            }),
            ({resource, meetingMode, videoControlActive}) => {
                const MeetingMode = this.mainController?.MeetingMode;
                const isVideoMode = meetingMode && MeetingMode && meetingMode === MeetingMode.video;
                const isImagesMode = meetingMode && MeetingMode && meetingMode === MeetingMode.images;
                const isOfflineMode = this.dashboardState?.isOfflineMode;
                const hasPreloadedImage = !!this.dashboardState?.videoModeState?.preloadedImage?.resource;

                // If meetingMode is undefined and we're not in offline mode, wait for it to be defined
                // This prevents the flicker when loading in video mode
                // Exception: If there's a preloaded image (edit mode from history), show it immediately
                const shouldWaitForMode = !meetingMode && !isOfflineMode && !hasPreloadedImage;

                // Don't show images if:
                // 1. The resource itself is a video (resource.isVideo)
                // 2. Video control is active (playing a video)
                // 3. We're waiting for meeting mode to be defined (prevents flicker)
                if (resource && !resource.isVideo && resource.url && !videoControlActive && !shouldWaitForMode) {
                    this.setImage(resource);
                }
            },
            {
                fireImmediately: true
            }
        );

        this.tagsChangedReactionDisposer = reaction(
            () => this.areTagsChanged,
            (areTagsChanged) => {
                if (this.dashboardState) {
                    runInAction(() => {
                        this.dashboardState.areTagsChanged = areTagsChanged;
                    });
                }
            },
            {fireImmediately: true}
        );
    }

    private _initImageModeWatcher(): void {
        // DON'T watch imageMode - let MainController handle tsScanArea initialization
        // This avoids timing issues with canvas availability
    }

    // Getters for observables and properties
    @computed
    get currentResource() {
        return this.dashboardState?.currentResource;
    }

    @computed
    get shouldShowImageEditor(): boolean {
        // Use dashboardState.currentResource (source of truth) instead of state.currentResource
        const hasResource = !!this.dashboardState?.currentResource?.url;
        const meetingMode = this.dashboardState?.meetingMode;
        const isOfflineMode = this.dashboardState?.isOfflineMode;
        const hasPreloadedImage = !!this.dashboardState?.videoModeState?.preloadedImage?.resource;
        const MeetingMode = this.mainController?.MeetingMode;
        const isImagesMode = meetingMode && MeetingMode && meetingMode === MeetingMode.images;
        const isOneClickMode = meetingMode && MeetingMode && meetingMode === MeetingMode.oneClick;
        const isImageEditMode = isImagesMode || isOneClickMode;

        // If we don't have a resource, don't show
        if (!hasResource) {
            return false;
        }

        // In offline mode, always show if we have a resource
        if (isOfflineMode) {
            return true;
        }

        // In images or oneClick mode, always show
        if (isImageEditMode) {
            return true;
        }

        // If there's a preloaded image (edit mode from history), show it
        if (hasPreloadedImage) {
            return true;
        }

        // In online mode, wait for meeting mode to be defined before showing
        // This prevents showing the image editor with a stale image when loading in video mode
        if (!meetingMode) {
            return false; // Wait for mode to be defined
        }

        // Once mode is defined, show the image editor (even in video mode for thumbnails)
        return true;
    }

    @computed
    get tagsOpen(): boolean {
        return this.imageEditorState.tagsOpen;
    }

    @computed
    get tags(): string[] {
        return this.imageEditorState.tags;
    }

    get maxTagLength(): number {
        return MAX_TAG_LENGTH;
    }

    @computed
    get isSelectionMode(): boolean {
        const result = this.dashboardState?.imageMode === IMAGE_MODES.SCANNING;
        return result;
    }

    @computed
    get enableAnnotation(): boolean {
        const result = !this.dashboardState?.isObserve;
        return result;
    }

    @computed
    get labelMode(): boolean {
        return this.imageEditorState.labelMode;
    }

    @action
    setLabelMode(value: boolean): void {
        this.imageEditorState.labelMode = value;
    }

    get isScanCaptureDesign(): boolean {
        const result = this.dashboardState?.isToggleEnabled_ScanCaptureDesign || false;
        return result;
    }

    get isRoundedFloatingVideo(): boolean {
        return this.dashboardState?.isToggleEnabled_RoundedFloatingVideo || false;
    }

    get isStretchedVideoMode(): boolean {
        return this.mainController?.isToggleEnabled_StretchedVideoMode || false;
    }

    get enableTagging(): boolean {
        const isMiniDashboard = this.dashboardController?.miniDashboard || false;
        const isTaggingEnabled = this.accountData?.protectedSettings?.imageTagging || false;
        return isTaggingEnabled && !isMiniDashboard;
    }

    @computed
    get isCanvasReadyWithImage(): boolean {
        return this.imageEditorState.isCanvasReadyWithImage;
    }

    @computed
    get showAnnotationBar(): boolean {
        const currentResource = this.dashboardState?.currentResource;
        const isOfflineMode = this.dashboardState?.isOfflineMode;
        const isObserve = this.dashboardState?.isObserve;

        // Access MobX observable properties correctly
        const isVideo = currentResource?.isVideo === true;
        const hasUrl = currentResource?.url;
        const videoControlActive = this.videoModeState?.isActive === true;

        // Get the meeting mode to determine if we're in video/image mode
        const meetingMode = this.dashboardState?.meetingMode;
        const MeetingMode = this.mainController?.MeetingMode;
        const isVideoMode = meetingMode && MeetingMode && meetingMode === MeetingMode.video;
        const isImagesMode = meetingMode && MeetingMode && meetingMode === MeetingMode.images;

        // Show annotation bar ONLY in offline mode (not in video or images modes)
        // Hide if: video resource, video control active, or in video/images mode
        const shouldShow = isOfflineMode && !isVideo && !videoControlActive;

        return shouldShow;
    }

    @computed
    get showSharingMode(): boolean {
        // Show sharing mode whenever there's a resource and quick share is enabled
        // But NOT in mini or embedded dashboards
        // Use dashboardState.currentResource (source of truth) instead of state.currentResource
        const hasResource = !!this.dashboardState?.currentResource?.url;
        const isQuickShareEnabled = this.dashboardController?.isQuickShareEnabled?.() || false;
        const isMiniDashboard = this.dashboardController?.miniDashboard || false;
        const isEmbeddedDashboard = this.dashboardController?.embeddedDashboard || false;

        const result = hasResource && isQuickShareEnabled && !isMiniDashboard && !isEmbeddedDashboard;

        return result;
    }

    get sharingModeActions(): any[] {
        return this.dashboardController?.historyResourcesController?.sharingModeActions || [];
    }

    get sharingModeData(): any {
        const data = this.dashboardState?.currentResource;
        const canvasData = data
            ? {
                  ...data,
                  type: 'dataUrl' as const,
                  useOriginalImage: false,
                  _sharingLocation: 'canvas' as const
              }
            : data;

        return canvasData;
    }

    get isShareResourceReady(): boolean {
        const data = this.sharingModeData; // Use the same tagged data
        if (!data || !this.dashboardController?.historyResourcesController) {
            return false;
        }
        const ready = this.dashboardController.historyResourcesController.isSelectResourceReady(data);

        return ready;
    }

    // Stable method references for sharing (not getters to avoid creating new functions on every render)
    onShareClickHandler = (e: any, data: any) => {
        const handler = this.dashboardController?.historyResourcesController?.onShareClick;
        if (handler) {
            return handler(e, data); // data already has _sharingLocation from sharingModeData getter
        }
    };

    onShareActionClickHandler = (e: any, actionType: string, data: any) => {
        const handler = this.dashboardController?.historyResourceActionClick;
        if (handler) {
            return handler(e, actionType, data); // Pass event as first parameter
        }
    };

    get onShareClick(): any {
        const hasHandler = !!this.dashboardController?.historyResourcesController?.onShareClick;

        return hasHandler ? this.onShareClickHandler : null;
    }

    get onShareActionClick(): any {
        const hasHandler = !!this.dashboardController?.historyResourceActionClick;

        return hasHandler ? this.onShareActionClickHandler : null;
    }

    get dashboardTags(): any[] {
        // Return tags from accountData if available
        return this.accountData?.tags || [];
    }

    @computed
    get areTagsChanged(): boolean {
        const currentTags = toJS(this.imageEditorState.tags);
        const originalTags = this.imageEditorState.originalTags;

        if (currentTags.length !== originalTags.length) {
            return true;
        }

        return !currentTags.every((tag, index) => tag === originalTags[index]);
    }

    // Methods
    @action
    setImage(image: any): void {
        const isSameImage = this.currentLoadedImageUrl === image.url;

        if (this.enableTagging) {
            const isSameResource = this.imageEditorState.tagsResourceUrl === image.url;
            // Use toJS for MobX; handle array-like objects (ObservableArray, etc.)
            const rawTags = toJS(image?.tags);
            const tagsFromResource = Array.isArray(rawTags)
                ? rawTags
                : rawTags && typeof (rawTags as any).length === 'number'
                  ? Array.from(rawTags as ArrayLike<any>)
                  : [];
            const hasTagsData = tagsFromResource.length > 0;

            if (hasTagsData) {
                const tagStrings = tagsFromResource
                    .map((tag: any) => (typeof tag === 'string' ? tag : tag?.text))
                    .filter(Boolean);

                this.imageEditorState.tags = tagStrings;
                this.imageEditorState.originalTags = [...tagStrings];
                this.imageEditorState.tagsResourceUrl = image.url;
                this.imageEditorState.tagsOpen = false;
            } else {
                // Always clear when no tags data - fixes stale state when isSameResource was true but resource lost tags
                this.imageEditorState.tags = [];
                this.imageEditorState.originalTags = [];
                this.imageEditorState.tagsResourceUrl = image.url;
                this.imageEditorState.tagsOpen = false;
            }
        }

        if (!this.annotateService?.canvas) {
            return;
        }

        if (!isSameImage) {
            runInAction(() => {
                this.imageEditorState.isCanvasReadyWithImage = false;
            });

            this.annotateService.setBackgroundImage(image.url);
            this.currentLoadedImageUrl = image.url;

            if (this.enableTagging) {
                this.annotateService.setTags(toJS(this.imageEditorState.tags));
            }
        }
    }

    @action
    closeTags(): void {
        this.imageEditorState.tagsOpen = false;
    }

    @action
    openTagsPanel(e?: Event): void {
        this.imageEditorState.tagsOpen = true;
        this.imageEditorState.tags = this.annotateService.getTags();
        e?.stopPropagation();
    }

    @action
    toggleTags(): void {
        if (this.imageEditorState.tagsOpen) {
            this.closeTags();
        } else {
            this.openTagsPanel();
        }
    }

    handleTagButtonClick(e: Event): void {
        this.openTagsPanel(e);
    }

    @action
    updateTags(tags: string[]): void {
        this.imageEditorState.tags = tags;
        // Convert MobX observable to plain array before passing to annotateService
        this.annotateService.setTags(toJS(tags));
    }

    private initScanMode(canvas: HTMLCanvasElement): void {
        if (!this.tsScanArea) {
            return;
        }

        // Use upperCanvasEl like the old directive does
        const upperCanvas = this.annotateService?.canvas?.upperCanvasEl;
        if (!upperCanvas) {
            return;
        }

        this.annotateService.setTool(null);
        this.tsScanArea.init(upperCanvas);
    }

    private destroyScanMode(): void {
        this.tsScanArea?.destroy();
    }

    // communication with main controller
    setMainController(mainController: any): void {
        (this as any).mainController = mainController;
    }

    setContainer(container: HTMLDivElement) {
        this.container = container;
    }

    setCanvas(canvas: HTMLCanvasElement) {
        this.canvas = canvas;

        this.currentLoadedImageUrl = null;

        runInAction(() => {
            this.imageEditorState.isCanvasReadyWithImage = false;
        });

        if (this.imageEditorState.disablePadding) {
            this.annotateService.removePadding();
        }

        const options = this.computeCanvasOptions();

        runInAction(() => {
            this.imageEditorState.isCanvasReadyWithImage = false;
        });

        // Pass the active tool to setCanvas so it can be restored during initialization
        // This ensures the tool state persists across mode switches.
        // Observers must always have null tool to prevent them from drawing annotations.
        const activeAnnotationTool = this.dashboardState?.isObserve ? null : this.imageEditorState.activeAnnotationTool;
        this.annotateService.setCanvas(canvas, options, activeAnnotationTool);

        // If we have a currentResource waiting, set it now
        // Use dashboardState.meetingMode (observable) instead of chatApi.client.mode
        const meetingMode = this.dashboardState?.meetingMode;
        const isOfflineMode = this.dashboardState?.isOfflineMode;
        const hasPreloadedImage = !!this.dashboardState?.videoModeState?.preloadedImage?.resource;
        const currentResource = this.dashboardState?.currentResource;
        const shouldSetImage =
            (meetingMode || isOfflineMode || hasPreloadedImage) && currentResource?.url && !currentResource.isVideo;

        if (shouldSetImage) {
            this.setImage(currentResource);
        }
    }

    private computeCanvasOptions() {
        const {newUi, disablePadding} = this.imageEditorState;
        const mediaFrameHeightPadding = this.tsInterfaceHelper.mediaFrameHeightPadding;

        const containerWidth = this.container?.offsetWidth || 0;
        const containerHeight = this.container?.offsetHeight || 0;
        const parentWidth = this.container?.parentElement?.offsetWidth || 0;
        const parentHeight = this.container?.parentElement?.offsetHeight || 0;

        if (this.isMobile || disablePadding) {
            // For mini dashboard (disablePadding), use parent size to match video sizing
            // Account for video container padding (12px on each side = 24px total)
            // For mobile, use container size
            let maxWidth, maxHeight;
            if (disablePadding && parentWidth > 0 && parentHeight > 0) {
                maxWidth = parentWidth - 24;
                maxHeight = parentHeight - 24;
            } else {
                maxWidth = containerWidth || this.window.innerWidth;
                maxHeight = containerHeight || this.window.innerHeight;
            }

            return {
                width: maxWidth,
                height: maxHeight,
                maxWidth,
                maxHeight,
                allowedRatios: mediaFrameUIParams.ALLOWED_RATIOS
            };
        }

        const widthImagePadding = newUi
            ? mediaFrameUIParams.WIDTH_PADDING_IMAGES
            : mediaFrameUIParams.WIDTH_PADDING_IMAGES_OLD_UI;

        return {
            width: this.window.innerWidth - widthImagePadding,
            height: this.window.innerHeight - mediaFrameHeightPadding,
            maxWidth: this.window.innerWidth - widthImagePadding,
            maxHeight: this.window.innerHeight - mediaFrameHeightPadding,
            allowedRatios: mediaFrameUIParams.ALLOWED_RATIOS
        };
    }
}
